import * as babylon from "@babylonjs/core";

import {
  FALLBACK_EXTENT_M,
  modelBoundsOf,
  type ModelBounds,
} from "./modelFraming";

/**
 * The model's size and centre, in world space.
 *
 * Measured over the drawable meshes only: a URDF export's link nodes carry no
 * geometry — they exist to give their children a frame — and including their
 * zero-sized bounds would drag the centre toward whichever link happened to sit
 * furthest out.
 *
 * World rather than local, so the glTF-to-Babylon handedness conversion that the
 * synthetic `__root__` node carries is folded in. World matrices are computed
 * here rather than assumed: a container from `ModelLoader.load` was never added
 * to a scene, so nothing maintains them.
 *
 * A model with no drawable geometry yields {@link FALLBACK_EXTENT_M} at the
 * origin rather than an empty or infinite box.
 *
 * Reads a container, never the live panel scene. Measuring drawn geometry is
 * legal in response to a user gesture and illegal on a frame — `constrainCamera`
 * caches its envelope on `entityGeneration` for exactly that reason — and a
 * container loaded into a throwaway `NullEngine` scene cannot reach either.
 *
 * `orientation` is the loader's correction into the scene standard
 * (`LoadedModel.orientation`). The panel applies it above the copy's roots, so
 * the box is turned by it here to measure the model as it will be drawn.
 */
export function measureModel(
  container: babylon.AssetContainer,
  orientation?: babylon.Quaternion,
): ModelBounds {
  let min: babylon.Vector3 | undefined;
  let max: babylon.Vector3 | undefined;

  for (const mesh of container.meshes) {
    if (mesh.getTotalVertices() === 0) {
      continue;
    }
    mesh.computeWorldMatrix(true);
    const box = mesh.getBoundingInfo().boundingBox;
    min = min
      ? babylon.Vector3.Minimize(min, box.minimumWorld)
      : box.minimumWorld.clone();
    max = max
      ? babylon.Vector3.Maximize(max, box.maximumWorld)
      : box.maximumWorld.clone();
  }

  if (!min || !max) {
    return { extent: FALLBACK_EXTENT_M, center: { x: 0, y: 0, z: 0 } };
  }
  if (orientation) {
    [min, max] = turnedBox(min, max, orientation);
  }
  return modelBoundsOf(min, max);
}

/** The axis-aligned box around `min`..`max` once every corner is turned. */
function turnedBox(
  min: babylon.Vector3,
  max: babylon.Vector3,
  orientation: babylon.Quaternion,
): [babylon.Vector3, babylon.Vector3] {
  const corners = [0, 1, 2, 3, 4, 5, 6, 7].map((i) =>
    new babylon.Vector3(
      i & 1 ? max.x : min.x,
      i & 2 ? max.y : min.y,
      i & 4 ? max.z : min.z,
    ).applyRotationQuaternion(orientation),
  );
  return [
    corners.reduce((a, b) => babylon.Vector3.Minimize(a, b)),
    corners.reduce((a, b) => babylon.Vector3.Maximize(a, b)),
  ];
}
